/**
 * ThemeProvider — manages light/dark theme state across the app.
 *
 * Strategy:
 *   - Theme state lives in React context.
 *   - On mount: read localStorage, fall back to "dark" (current default).
 *   - On change: write `data-theme` attribute on <html> + persist to localStorage.
 *
 * Usage:
 *   1. Wrap your app in <ThemeProvider>...</ThemeProvider>
 *   2. Call useTheme() in any component for { theme, setTheme, toggle }
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "intal:theme";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): Theme {
  // SSR / no-window safety
  if (typeof window === "undefined") return "dark";

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // ignore quota / privacy errors
  }

  // Default: dark (per product decision)
  return "dark";
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readInitialTheme());

  // Apply on mount + when theme changes
  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Soft fail outside provider
    return {
      theme: "dark",
      setTheme: () => undefined,
      toggle: () => undefined
    };
  }
  return ctx;
}
